/**
 * ComparisonSortControl Component
 *
 * Dropdown control for sorting comparisons by Recent, Oldest, Favorites, Title, or Winner Score.
 * Persists selection in URL hash for deep linking.
 */

import React, { useState, useEffect } from 'react';
import { logger } from '../../../lib/logging';
import type { ComparisonSortOption } from '../../../utils/comparators';

export interface ComparisonSortControlProps {
  value: ComparisonSortOption;
  onChange: (sort: ComparisonSortOption) => void;
  className?: string;
  hideScoreOption?: boolean; // Hide "Best Winner Score" if no score data available
  isAnonymous?: boolean;
}

const SORT_OPTIONS: { value: ComparisonSortOption; label: string; icon: string }[] = [
  {
    value: 'recent',
    label: 'Recent First',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    value: 'oldest',
    label: 'Oldest First',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    value: 'favorites',
    label: 'Favorites First',
    icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
  },
  {
    value: 'title',
    label: 'Title (A-Z)',
    icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12'
  },
  {
    value: 'score',
    label: 'Best Winner Score',
    icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z'
  }
];

export const ComparisonSortControl: React.FC<ComparisonSortControlProps> = ({
  value,
  onChange,
  className = '',
  hideScoreOption = false,
  isAnonymous = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prevSort, setPrevSort] = useState<ComparisonSortOption>(value);

  const availableOptions = SORT_OPTIONS.filter(opt => {
    if (hideScoreOption && opt.value === 'score') {
      return false;
    }

    if (isAnonymous && (opt.value === 'favorites' || opt.value === 'score')) {
      return false;
    }

    return true;
  });

  const currentOption = availableOptions.find(opt => opt.value === value) || availableOptions[0];

  const handleChange = (option: ComparisonSortOption) => {
    setPrevSort(value);
    onChange(option);
    setIsOpen(false);

    logger.info('comparisons_sort_changed', undefined, {
      op: 'comparisons_sort_changed',
      component: 'ComparisonSortControl',
      sort_key: option,
      prev_sort_key: prevSort,
      source_page: 'results#comparisons',
      server_sorted: false
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-comparison-sort-control]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} data-comparison-sort-control>
      {/* Sort Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg hover:bg-slate-800 hover:border-cyan-500/30 transition-all duration-200 text-sm font-medium text-slate-200"
        aria-label="Sort comparisons"
        aria-expanded={isOpen}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={currentOption.icon}
          />
        </svg>
        <span>{currentOption.label}</span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-52 bg-slate-800 border border-slate-700/50 rounded-lg shadow-xl z-10 overflow-hidden">
          {availableOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleChange(option.value)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-sm transition-colors ${
                value === option.value
                  ? 'bg-cyan-500/20 text-cyan-300 font-medium'
                  : 'text-slate-200 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={option.icon}
                />
              </svg>
              <span>{option.label}</span>
              {value === option.value && (
                <svg className="w-4 h-4 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
